import classNames from 'classnames';
import type { ButtonHTMLAttributes } from 'react';

import { useUiMessages } from '../../messages';
import { Icon } from '../Icon';

import styles from './CloseButton.module.css';

export type CloseButtonSize = 'sm' | 'md' | 'lg';

export interface CloseButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children'
> {
  size?: CloseButtonSize;
  /** Style for placement on a dark surface. */
  onDark?: boolean;
  /** Accessible name — pass a localized string; defaults to `"Close"`. */
  label?: string;
}

/**
 * Square icon button carrying the `x-close` glyph — dialog/toast/panel dismissal.
 *
 * @interim Ported from the reference design; replaceable by the UI Kit without API breakage.
 */
export function CloseButton({
  size = 'md',
  onDark = false,
  label,
  className,
  type = 'button',
  ...rest
}: CloseButtonProps) {
  const messages = useUiMessages();
  const resolvedLabel = label ?? messages.closeButton?.close ?? 'Close';
  return (
    <button
      type={type}
      aria-label={resolvedLabel}
      className={classNames(
        styles.close,
        styles[`size-${size}`],
        onDark && styles['on-dark'],
        className,
      )}
      {...rest}
    >
      <Icon name="x-close" />
    </button>
  );
}
